import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminPage() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/pending-users`);
      setPendingUsers(response.data);
    } catch (error) {
      console.error('Errore nel caricamento utenti:', error);
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.post(`${API_URL}/admin/approve-user/${userId}`);
      toast.success('Utente approvato!');
      fetchPendingUsers();
    } catch (error) {
      toast.error('Errore nell\'approvazione');
    }
  };

  const handleReject = async (userId) => {
    try {
      await axios.delete(`${API_URL}/admin/reject-user/${userId}`);
      toast.success('Utente rifiutato ed eliminato');
      fetchPendingUsers();
    } catch (error) {
      toast.error('Errore nel rifiuto');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 md:p-8 overflow-y-auto pb-24 md:pb-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto space-y-8"
      >
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">👨‍💼 Pannello Admin</h1>
          <p className="text-muted-foreground">
            Gestisci le richieste di registrazione
          </p>
        </div>

        {pendingUsers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Nessun utente in attesa</p>
              <p className="text-sm mt-2">Tutti gli utenti sono stati gestiti</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-base px-3 py-1">
                {pendingUsers.length} {pendingUsers.length === 1 ? 'richiesta' : 'richieste'}
              </Badge>
            </div>
            {pendingUsers.map((user, index) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-xl">{user.name}</CardTitle>
                        <CardDescription className="space-y-1">
                          <div>{user.email}</div>
                          <div className="text-xs">Richiesta: {formatDate(user.created_at)}</div>
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className="text-orange-600 border-orange-600">
                        In attesa
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleApprove(user.id)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid={`approve-user-${user.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approva
                      </Button>
                      <Button
                        onClick={() => handleReject(user.id)}
                        variant="destructive"
                        className="flex-1"
                        data-testid={`reject-user-${user.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Rifiuta
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
